import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Mail, RefreshCcw, Search, Clock, AlertTriangle, CheckCircle2, XCircle, Inbox, Settings } from 'lucide-react';
import { toast } from 'sonner';
import AdminEmailSettingsPane from '@/components/admin/AdminEmailSettingsPane';

const BACKEND_URL = '';

const STATUS_META = {
  sent: { label: 'Sent', class: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  queued: { label: 'Queued', class: 'bg-slate-100 text-slate-600', Icon: Clock },
  failed: { label: 'Failed', class: 'bg-rose-100 text-rose-700', Icon: XCircle },
  not_configured: { label: 'Not configured', class: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
};

const CATEGORIES = [
  { key: '', label: 'All' },
  { key: 'auth', label: 'Auth' },
  { key: 'event', label: 'Events' },
  { key: 'britelab', label: 'BriteLabs' },
  { key: 'forum', label: 'Forum' },
  { key: 'message', label: 'Messages' },
  { key: 'system', label: 'System' },
];

function StatPill({ icon: Icon, label, value, tone }) {
  const toneClass = {
    navy: 'border-brand-navy/20 bg-brand-navy/5 text-brand-navy',
    sage: 'border-brand-sage/30 bg-brand-sage/10 text-emerald-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    slate: 'border-slate-200 bg-white text-slate-700',
  }[tone] || 'border-slate-200 bg-white text-slate-700';
  return (
    <Card className={`p-4 border ${toneClass} flex items-center gap-3`}>
      <Icon className="h-5 w-5 opacity-70" />
      <div>
        <p className="text-[11px] uppercase tracking-wider opacity-70 font-semibold">{label}</p>
        <p className="text-xl font-bold leading-tight">{value}</p>
      </div>
    </Card>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function AdminCommsTab() {
  return (
    <Tabs defaultValue="log" className="w-full" data-testid="admin-comms-tabs">
      <TabsList className="mb-4">
        <TabsTrigger value="log" data-testid="comms-subtab-log">
          <Inbox className="h-4 w-4 mr-1.5" /> Email Log
        </TabsTrigger>
        <TabsTrigger value="settings" data-testid="comms-subtab-settings">
          <Settings className="h-4 w-4 mr-1.5" /> Routing & Templates
        </TabsTrigger>
      </TabsList>
      <TabsContent value="log">
        <EmailLogSubTab />
      </TabsContent>
      <TabsContent value="settings">
        <AdminEmailSettingsPane />
      </TabsContent>
    </Tabs>
  );
}


function EmailLogSubTab() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadStats = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/comms/stats`, { credentials: 'include' });
      if (r.ok) setStats(await r.json());
    } catch { /* ignore */ }
  };

  const loadRows = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '50' });
      if (status) params.set('status', status);
      if (category) params.set('category', category);
      if (search.trim()) params.set('search', search.trim());
      const r = await fetch(`${BACKEND_URL}/api/admin/comms/emails?${params}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setRows(d.items || []);
        setTotal(d.total || 0);
        setPages(d.pages || 1);
        setPage(d.page || 1);
      } else {
        toast.error('Failed to load email log');
      }
    } catch {
      toast.error('Failed to load email log');
    }
    setLoading(false);
  };

  useEffect(() => { loadStats(); loadRows(1); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRows(1); /* eslint-disable-next-line */ }, [status, category]);

  const openDetail = async (row) => {
    setSelected({ ...row, _loadingHtml: true });
    setDetailLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/comms/emails/${row.log_id}`, { credentials: 'include' });
      if (r.ok) {
        setSelected(await r.json());
      } else {
        toast.error('Failed to load email body');
      }
    } catch {
      toast.error('Failed to load email body');
    }
    setDetailLoading(false);
  };

  const statusEntries = useMemo(() => Object.entries(stats?.by_status || {}), [stats]);
  const categoryEntries = useMemo(() => Object.entries(stats?.by_category || {}), [stats]);

  return (
    <div className="space-y-6" data-testid="admin-comms-pane-inner">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold font-heading">Comms — Email audit log</h2>
          <p className="text-sm text-slate-500 mt-0.5">Every email sent from the platform, including routing and activation times.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadStats(); loadRows(page); }} data-testid="comms-refresh">
          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill icon={Inbox} label="Last 24h" value={stats?.last_24h ?? '—'} tone="navy" />
        <StatPill icon={Mail} label="Last 30 days" value={stats?.last_30d ?? '—'} tone="slate" />
        <StatPill icon={Clock} label="Avg activation" value={stats ? `${stats.avg_duration_ms}ms` : '—'} tone="sage" />
        <StatPill icon={AlertTriangle} label="Failed (30d)" value={stats?.by_status?.failed ?? 0} tone={(stats?.by_status?.failed || 0) > 0 ? 'rose' : 'slate'} />
      </div>

      {/* Breakdown rows */}
      {stats && (
        <Card className="p-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Status (30d)</p>
              <div className="flex flex-wrap gap-2">
                {statusEntries.length === 0 && <span className="text-xs text-slate-400">No emails sent yet.</span>}
                {statusEntries.map(([s, n]) => {
                  const meta = STATUS_META[s] || { label: s, class: 'bg-slate-100 text-slate-600' };
                  return (
                    <span key={s} className={`text-xs px-2.5 py-1 rounded-full font-medium ${meta.class}`}>
                      {meta.label} · {n}
                    </span>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Category (30d)</p>
              <div className="flex flex-wrap gap-2">
                {categoryEntries.length === 0 && <span className="text-xs text-slate-400">—</span>}
                {categoryEntries.map(([c, n]) => (
                  <span key={c} className="text-xs px-2.5 py-1 rounded-full font-medium bg-brand-navy/5 text-brand-navy border border-brand-navy/20">
                    {c} · {n}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 mr-1">Category:</span>
          {CATEGORIES.map(c => (
            <button key={c.key || 'all'}
              onClick={() => setCategory(c.key)}
              data-testid={`comms-cat-${c.key || 'all'}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                category === c.key ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
              }`}>{c.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 mr-1">Status:</span>
          {['', 'sent', 'failed', 'queued', 'not_configured'].map(s => (
            <button key={s || 'all'}
              onClick={() => setStatus(s)}
              data-testid={`comms-status-${s || 'all'}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                status === s ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
              }`}>{s ? (STATUS_META[s]?.label || s) : 'All'}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); loadRows(1); }} className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by recipient email or subject…"
            data-testid="comms-search"
            className="max-w-md"
          />
          <Button type="submit" variant="outline" size="sm">Search</Button>
        </form>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <tr>
                <th className="text-left font-semibold py-2.5 px-3">Queued</th>
                <th className="text-left font-semibold py-2.5 px-3">To</th>
                <th className="text-left font-semibold py-2.5 px-3">Subject</th>
                <th className="text-left font-semibold py-2.5 px-3">Category</th>
                <th className="text-left font-semibold py-2.5 px-3">Status</th>
                <th className="text-left font-semibold py-2.5 px-3">Activation</th>
                <th className="text-left font-semibold py-2.5 px-3">Sent</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan="7" className="text-center text-slate-400 py-8">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="7" className="text-center text-slate-400 py-8">No emails match these filters.</td></tr>
              )}
              {rows.map(r => {
                const meta = STATUS_META[r.status] || { label: r.status || 'unknown', class: 'bg-slate-100 text-slate-600', Icon: Mail };
                const Icon = meta.Icon;
                return (
                  <tr key={r.log_id}
                    onClick={() => openDetail(r)}
                    data-testid={`comms-row-${r.log_id}`}
                    className="border-b border-slate-100 cursor-pointer hover:bg-brand-navy/5 transition-colors">
                    <td className="py-2.5 px-3 text-xs text-slate-500 whitespace-nowrap">{formatDate(r.queued_at)}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-700 max-w-[200px] truncate">{r.to_email}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-700 max-w-[340px] truncate">{r.subject}</td>
                    <td className="py-2.5 px-3 text-xs"><Badge variant="secondary" className="text-[10px]">{r.category}</Badge></td>
                    <td className="py-2.5 px-3 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${meta.class}`}>
                        <Icon className="h-3 w-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-slate-500">{r.duration_ms != null ? `${r.duration_ms}ms` : '—'}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-500 whitespace-nowrap">{formatDate(r.sent_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs">
            <span className="text-slate-500">Page {page} of {pages} — {total} total</span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => loadRows(page - 1)} data-testid="comms-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => loadRows(page + 1)} data-testid="comms-next">Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="comms-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="leading-tight">{selected.subject}</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 text-xs mt-2">
                <div><span className="font-semibold text-slate-500">To:</span> <span className="text-slate-700">{selected.to_email}</span></div>
                <div><span className="font-semibold text-slate-500">From:</span> <span className="text-slate-700">{selected.from_email}</span></div>
                <div><span className="font-semibold text-slate-500">Queued:</span> <span className="text-slate-700">{formatDate(selected.queued_at)}</span></div>
                <div><span className="font-semibold text-slate-500">Sent:</span> <span className="text-slate-700">{formatDate(selected.sent_at)}</span></div>
                <div><span className="font-semibold text-slate-500">Activation:</span> <span className="text-slate-700">{selected.duration_ms != null ? `${selected.duration_ms}ms` : '—'}</span></div>
                <div><span className="font-semibold text-slate-500">Provider:</span> <span className="text-slate-700">{selected.provider} {selected.provider_status_code ? `· ${selected.provider_status_code}` : ''}</span></div>
                <div><span className="font-semibold text-slate-500">Category:</span> <span className="text-slate-700">{selected.category}</span></div>
                <div><span className="font-semibold text-slate-500">Source:</span> <span className="text-slate-700">{selected.source || '—'}</span></div>
                <div className="col-span-2"><span className="font-semibold text-slate-500">Status:</span> <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${(STATUS_META[selected.status] || {}).class || 'bg-slate-100'}`}>{(STATUS_META[selected.status] || {}).label || selected.status}</span></div>
                {selected.error && (
                  <div className="col-span-2"><span className="font-semibold text-rose-600">Error:</span> <span className="text-rose-700">{selected.error}</span></div>
                )}
              </div>
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 mb-2">Body preview ({selected.html_size?.toLocaleString() || 0} bytes)</p>
                {detailLoading ? (
                  <p className="text-xs text-slate-400">Loading email body…</p>
                ) : selected.html_content ? (
                  <iframe
                    title="email-preview"
                    srcDoc={selected.html_content}
                    sandbox=""
                    className="w-full h-[60vh] border border-slate-200 rounded-lg bg-white"
                    data-testid="comms-body-iframe"
                  />
                ) : (
                  <p className="text-xs text-slate-400">No body stored for this entry.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
